import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { AuthContext } from "../Auth";
import { setSignalDataSPEI, getDataSPEI } from "../services/Payments/getDataSPEI";
import { Error500 } from "../alerts/Error";

//componentes
import Header from "../components/Header";
import Loader from "../components/Loader";
import ButtonCopy from "../components/Button/ButtonCopy";
// assets
import logoNBX from "../images/neubox-logo.png";
import logoSPEI from "../images/spei.svg";
import ic_copy from "../images/icon/ic-copy.svg";
import download_icon from "../images/icon/ic-download-blue.svg";
// styles
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/SPEI.scss";

const IconCopyLeft = () => {
  return (
    <img
      src={ic_copy}
      alt="icono copiar"
      style={{
        marginRight: "4px",
        width: "15px",
        height: "15px",
        marginTop: "-2px",
      }}
    />
  );
};

const formatPrice = (precio = 0) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(precio);
};

const VoucherSpei = () => {
  const { t } = useTranslation();
  const { invoiceId } = useParams();
  const { userAuth, setShowLoginExpired } = useContext(AuthContext);
  const [loader, setLoader] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [importe, setImporte] = useState(null);
  const [clabe, setClabe] = useState("");

  const urlClientes = process.env.REACT_APP_CLIENTES_BASE_URL;

  const handlerLoaderShow = () => {
    Swal.close();
    setLoader(true);
  };

  const handlerLoaderHide = () => {
    setLoader(false);
  };

  // redirecciona a viewinvoice
  const redirectToErrorView = () => {
    window.location.replace(urlClientes + "viewinvoice.php?id=" + invoiceId + "&errortransfer=true");
  };

  const redirectToHome = () => {
    window.location.replace(urlClientes);
  };

  useEffect(() => {
    const abortController = new AbortController();
    setSignalDataSPEI(abortController.signal);

    const init = async () => {
      handlerLoaderShow();
      // solicitamos informacion del invoice
      let dataSPEI = await getDataSPEI(invoiceId, userAuth?.token);
      if (dataSPEI) {
        if (dataSPEI.result === "error") {
          switch (dataSPEI.error) {
            case "session":
              setShowLoginExpired(true);
              break;

            case 'client':
              redirectToHome();
              break;

            default:
              Error500(() => {
                redirectToErrorView();
              });
              break;
          }
        }

        if (dataSPEI.result === "success") {
          if (dataSPEI.status === "Unpaid" && dataSPEI.paymentmethod === "spei") {
            setInvoice(dataSPEI);
            setClabe(dataSPEI.stp.clabe);
            setImporte(formatPrice(dataSPEI.stp.amount));
          } else {
            redirectToErrorView();
          }
        }
      }

      handlerLoaderHide();
    };

    if (userAuth?.token) {
      init();
    } else {
      // si no existe sesión, se le obliga a iniciar sesión al cliente
      setShowLoginExpired(true);
    }

    return () => {
      abortController.abort();
    };
  }, [userAuth]);

  return (
    <div className="cart-content spei">
      <Header onlyLogo={true} />

      <Loader open={loader} />

      {/* invoice spei */}
      {!loader && invoice !== null && invoice.paymentmethod === "spei" && (
        <div className="spei__card">
          <h1 className="spei__card--header">{t("spei.pago_pendiente")}</h1>
          <div className="px-4 px-md-5 mx-0 mx-sm-2 mx-md-3 mx-lg-4 mx-xl-5">
            <div className="spei__card--image">
              <img src={logoNBX} alt="Logo Neubox" />
            </div>

            <h1 className="spei__card--title">{t("spei.title")}</h1>

            <div className="spei__remember">
              <h2 className="spei__remember--title">{t("spei.remember.title")}</h2>
              <ul>
                <li dangerouslySetInnerHTML={{ __html: t("spei.remember.li_1") }} />
                <li dangerouslySetInnerHTML={{ __html: t("spei.remember.li_2") }} />
                <li>{t("spei.remember.li_3")}</li>
                <li dangerouslySetInnerHTML={{ __html: t("spei.remember.li_4") }} />
              </ul>
            </div>
            {/* alert warning */}

            <div className="spei__card-details">
              <div className="spei__card-details--header">
                <div className="spei__card-details--logo">
                  <img src={logoSPEI} alt="Logo SPEI" />
                </div>
                <h2 className="spei__card-details--title">{t("spei.details.title")}</h2>
                <p className="spei__card-details--subtitle">{t("spei.details.subtitle")}</p>
              </div>

              <div className="spei__card-details--body">
                <div className="spei__card-details--content">
                  <label>{t("spei.details.banco.label")}</label>
                  <p className="spei__card-details--text" dangerouslySetInnerHTML={{ __html: t("spei.details.banco.text") }} />
                </div>

                <div className="spei__card-details--content">
                  <label>{t("spei.details.clabe.label")}</label>
                  <div className="spei__card-details--copy">
                    <p className="spei__card-details--text blue-text">{clabe}</p>
                    <ButtonCopy
                      text={t("spei.btn.copy")}
                      copyValue={clabe}
                      className="spei__btn--copy"
                      successMessage={t("spei.details.clabe.copy.success")}
                      iconLeft={<IconCopyLeft />}
                    />
                  </div>
                </div>
                <div className="spei__card-details--content">
                  <label>
                    {t("spei.details.referencia.label")} <strong className="order--mobile">{t("spei.details.referencia.text")}</strong>
                  </label>
                  <div className="spei__card-details--copy">
                    <p className="spei__card-details--text blue-text">
                      {invoice.invoiceid}
                    </p>
                    <ButtonCopy
                      text={t("spei.btn.copy")}
                      copyValue={invoice.invoiceid}
                      className="spei__btn--copy"
                      successMessage={t("spei.details.referencia.copy.success")}
                      iconLeft={<IconCopyLeft />}
                    />
                  </div>
                </div>
                <div className="spei__card-details--content">
                  <label>{t("spei.details.importe.label")}</label>
                  <p className="spei__card-details--text">
                    $ {importe} MXN
                  </p>
                </div>
              </div>
            </div>
            {/* details */}

            <div className="spei__instructions">
              <label className="spei__instructions--title">{t("spei.instructions.title")}</label>
              <ol className="spei__instructions--list">
                <li dangerouslySetInnerHTML={{ __html: t("spei.instructions.li_1") }} />
                <li>{t("spei.instructions.li_2")}</li>
                <li>{t("spei.instructions.li_3")}</li>
                <li dangerouslySetInnerHTML={{ __html: t("spei.instructions.li_4") }} />
              </ol>
            </div>
            {/* instructions */}

            <div className="spei__buttons">
              <a
                href={urlClientes + "/viewinvoicepdf.php?id=" + invoiceId}
                className="btn btn-outline-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={download_icon} alt="icono descargar" />
                {t("spei.btn.download")}
              </a>
              <a className="btn btn-success" href={urlClientes}>
                {t("spei.btn.continue")}
              </a>
            </div>
            {/* buttons */}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherSpei;
